// Nomad-mon Go! — SC32 OPPONENT SCALING. Pure, deterministic, no DOM/audio.
//
// WHY THIS EXISTS
// The sim (scripts/engine-sim.js --sweep) measured how much each difficulty
// lever is actually worth against a competent player, on the 5+2 ruleset:
//
//   AI search depth   1 -> 2 ply ............  46.7% -> 42.5%   (~4 points)
//   AI mistake rate   0.0 -> 0.8 ............  44.0% -> 54.3%   (~10 points)
//   AI DECK POWER     p20 -> p95 ...........  86.7% ->  2.7%   (~84 points)
//
// Deck power dominates everything else by an order of magnitude. A smarter AI
// holding a weak hand is still a punching bag, which is exactly why the old
// build stayed easy no matter how good the player's collection got: the
// opponent was drawn uniformly at random from the whole card pool forever.
//
// So the opponent is drafted against the player's TOTAL five-card power. The
// full-strength tier targets below are calibrated against two player models,
// and a separate invisible onboarding term fades out over the first five wins.

// ---------------------------------------------------------------- power
import { clampLevel, levelCard } from './levelling.js';
// One number for "how much fight is in this card". Deliberately simple and
// stable: it must be computable identically on every client and must not drift
// when card art or text changes.
export function cardPower(c, level = c && c.level || 1) {
  if (!c || !c.moves || c.moves.length < 2) return 0;
  const d = levelCard(c, level);
  return d.hp
    + d.moves[0].dmg * 2      // basic is the move you actually spam
    + d.moves[1].dmg * 1.5    // special is conditional, so it counts for less
    + (d.spd || 0);
}

// SOLO SCALING CONTRACT: a deck is the TOTAL combat strength of the five
// selected fighters. Do not substitute collection power, the best fighter, or
// the median. Rank-matching below still prevents a one-bomb/four-scrubs draft
// from being answered by five identical median cards, while this aggregate is
// the explicit number the target, bracket and tests are built from.
export function deckPower(cardIds, byId, levels = {}) {
  return cardIds
    .slice(0, 5)
    .reduce((total, id) => total + cardPower(byId[id], levels[id] || 1), 0);
}

// ---------------------------------------------------------------- brackets
// The formula, stated once so it can be quoted in the UI and the log:
//
//   growth  = effectiveStreak * STREAK_STEP
//   target  = totalPower(player's five)
//             * (1 + growth + DIFF_BIAS[difficulty] + onboardingBias)
//   band    = target * (BAND_BASE + BAND_STEP * min(streak, STREAK_CAP))
//
// The opponent's five are drawn from the cards whose power falls inside
// [target - band, target + band], seeded by (seed, streak, difficulty) so the
// same player on the same streak with the same seed always meets the same
// opponent — deterministic, replayable, and safe for multiplayer lockstep.
//
// The BAND widens with the streak on purpose: early opponents are a mirror of
// what you brought, and a long streak starts throwing genuine outliers at you.
// Tuned against the sim, not guessed. cardPower is calibrated (a x1.00 power
// ratio produces a measured 50.3% win-rate between equal policies, x1.10 gives
// 36%), so the streak ramp is deliberately gentle: +2% per win to a +12% cap.
// The first pass used +4.5% to +36% and it was brutal — a 4-win streak dropped
// a good player to an 18% win-rate, which ends streaks by frustration rather
// than by difficulty.
export const STREAK_STEP = 0.02;    // +2% opponent power per post-ramp consecutive win
export const STREAK_CAP = 6;        // caps at +12% — a real ramp, still winnable
export const BAND_BASE = 0.10;
export const BAND_STEP = 0.02;
// NORMAL is calibrated against the human-like referee in engine-sim, not the
// competent search agent: -10% puts that new-player model at 55-60%, while the
// strong agent wins well above it. EASY uses the same squad budget with a much
// weaker action policy. WRATH stays +6% and receives no onboarding assistance.
export const DIFF_BIAS = { easy: -0.10, normal: -0.10, wrath: 0.06 };

// INVISIBLE NEW-PLAYER RAMP. At 0/1/2/3/4/5 solo wins EASY and NORMAL receive
// an additional -10/-8/-6/-4/-2/0 percentage points of opponent power. Streak
// pressure is held at zero through win five, then grows smoothly from +2% on
// win six. A loss never removes onboarding progress, so repeated first-match
// losses do not snap the player back to full difficulty. WRATH is exempt.
export const NEW_PLAYER_RAMP_WINS = 5;
export const NEW_PLAYER_START_BIAS = -0.10;
export function newPlayerBias(wins, difficulty = 'normal') {
  if (difficulty === 'wrath') return 0;
  const w = Math.max(0, Math.min(NEW_PLAYER_RAMP_WINS, Number(wins) || 0));
  return NEW_PLAYER_START_BIAS * (1 - w / NEW_PLAYER_RAMP_WINS);
}

// Human-facing bracket names, keyed off how the opponent compares to you.
const BRACKET_NAMES = [
  { max: -0.12, key: 'outmatched', label: 'OUTMATCHED', blurb: 'Shao fields a weaker squad.' },
  { max: 0.06, key: 'even', label: 'EVEN MATCH', blurb: 'Shao mirrors your squad.' },
  { max: 0.20, key: 'favoured', label: 'SHAO FAVOURED', blurb: 'Shao brings the better five.' },
  { max: 0.34, key: 'elite', label: 'ELITE', blurb: 'Shao fields elite cards.' },
  { max: Infinity, key: 'nightmare', label: 'NIGHTMARE', blurb: "Shao's best. Good luck." },
];
export function bracketFor(ratio) {
  return BRACKET_NAMES.find(b => ratio <= b.max) || BRACKET_NAMES[BRACKET_NAMES.length - 1];
}

// mulberry32, duplicated here so scaling.js has no import cycle with engine.js.
function rngFrom(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function hashStr(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h >>> 0;
}

// ---------------------------------------------------------------- the draft
// pool: array of character card objects the opponent may draw from.
// sups: array of support card objects.
// playerChars: the player's five card ids. streak: consecutive wins (>= 0).
export function draftOpponent({
  playerChars, pool, sups, byId, streak = 0, difficulty = 'normal', seed = 1,
  nChars = 5, nSupports = 2, biasOverride = null, playerLevels = {},
  newPlayerWins = NEW_PLAYER_RAMP_WINS,
}) {
  const rng = rngFrom((seed | 0) ^ hashStr(`${difficulty}:${streak}`));
  const mine = deckPower(playerChars, byId, playerLevels) || 1;
  const rawStreak = Math.max(0, Number(streak) || 0);
  const wins = Math.max(0, Number(newPlayerWins) || 0);
  // Before onboarding is complete, consecutive wins are the onboarding ramp;
  // counting them again as streak pressure would cancel the assistance. Once
  // complete, only wins beyond the first five can feed the current streak.
  const rampStreak = difficulty === 'wrath' ? rawStreak
    : Math.min(rawStreak, Math.max(0, wins - NEW_PLAYER_RAMP_WINS));
  const s = Math.max(0, Math.min(rampStreak, STREAK_CAP));
  const growth = s * STREAK_STEP;
  const bias = biasOverride != null ? biasOverride : (DIFF_BIAS[difficulty] ?? 0);
  // Explicit bias overrides are authored encounters (the gauntlet); they must
  // not accidentally inherit the general solo onboarding ramp.
  const onboardingBias = biasOverride != null ? 0 : newPlayerBias(wins, difficulty);
  const scale = 1 + growth + bias + onboardingBias;
  const target = mine * scale;

  const taken = new Set(playerChars);
  const eligible = pool.filter(c => !taken.has(c.id));
  const shuffled = (arr) => arr.map(c => [rng(), c]).sort((a, b) => a[0] - b[0]).map(p => p[1]);

  // TOTAL-TARGETED, RANK-MATCHED DRAFT. Matching only the median was measurably unfair: a deck
  // of five cards tightly clustered at the median beats a deck with the same
  // median but real spread, because with five characters and three KOs to lose,
  // your two WEAKEST cards are exactly what gets picked off. Median-matching
  // handed the AI a 26% player win-rate while claiming the decks were "even".
  //
  // So the opponent's requested TOTAL is divided across the exact five shares
  // the player selected, then drafted rank for rank. Because those shares sum
  // to `mine`, the five `want` values sum exactly to `target`; rank matching is
  // the distribution guard, not a competing median aggregate.
  const myRanks = playerChars.slice(0, nChars)
    .map(id => ({ id, level: clampLevel(playerLevels[id]), power: cardPower(byId[id], playerLevels[id] || 1) }))
    .sort((a, b) => b.power - a.power);

  const used = new Set();
  const picks = [];
  const opponentLevels = {};
  for (const rank of myRanks.slice(0, nChars)) {
    const want = rank.power * scale;
    const band = Math.max(8, want * (BAND_BASE + BAND_STEP * s));
    const inBand = eligible.filter(c =>
      !used.has(c.id) && Math.abs(cardPower(c, rank.level) - want) <= band);
    let choice = shuffled(inBand)[0];
    if (!choice) {
      // band too thin at this rank — take the nearest available power instead,
      // so the draft never fails and never reverts to uniform-random.
      choice = eligible.filter(c => !used.has(c.id))
        .sort((a, b) => Math.abs(cardPower(a, rank.level) - want) - Math.abs(cardPower(b, rank.level) - want))[0];
    }
    if (!choice) break;
    used.add(choice.id);
    picks.push(choice);
    opponentLevels[choice.id] = rank.level;
  }
  const supports = shuffled(sups).slice(0, nSupports).map(c => c.id);
  const theirs = picks.map(c => c.id);
  const ratio = (deckPower(theirs, byId, opponentLevels) / mine) - 1;
  return {
    chars: theirs, supports, levels: opponentLevels,
    playerPower: mine, opponentPower: deckPower(theirs, byId, opponentLevels),
    target, scale, streak: s, difficulty, newPlayerWins: wins, onboardingBias,
    ratio, bracket: bracketFor(ratio),
  };
}

// ---------------------------------------------------------------- rewards
// A visible reason to fight up a bracket. Kept deliberately small so the
// economy cannot be farmed: the baseline win is 1 pack credit, and everything
// else is a bounded bonus. The server re-derives this for named players; the
// client only ever DISPLAYS it.
export const REWARD_BASE = 1;
const BRACKET_BONUS = { outmatched: 0, even: 0, favoured: 1, elite: 1, nightmare: 2 };
const DIFF_BONUS = { easy: 0, normal: 0, wrath: 1 };

export function battleReward({ bracketKey, difficulty = 'normal', streak = 0, won }) {
  if (!won) return { packs: 0, parts: [], total: 0 };
  const parts = [{ label: 'Victory', packs: REWARD_BASE }];
  const b = BRACKET_BONUS[bracketKey] || 0;
  if (b) parts.push({ label: bracketFor(0).key === bracketKey ? 'Bracket' : `Bracket bonus`, packs: b });
  const d = DIFF_BONUS[difficulty] || 0;
  if (d) parts.push({ label: "Shao's Wrath", packs: d });
  // streak pays at thresholds, not every win: 3, 5, 8.
  let sBonus = 0;
  const next = streak + 1;                 // the win being scored
  if (next === 3 || next === 5 || next === 8) sBonus = 1;
  if (sBonus) parts.push({ label: `${next}-win streak`, packs: sBonus });
  const total = Math.min(REWARD_BASE + b + d + sBonus, 5);   // hard cap
  return { packs: total, parts, total, streakAfter: next };
}
